import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Switch,
  TextField,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import moment from "moment";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  BulkSendSettingsStatus,
  OrganizationBulkSendSettings,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import TimezoneAutocomplete from "components/TimezoneAutocomplete";
import { Template } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { getLocation } from "utils/influencer";
import { trackEvent } from "utils/tracking";

import RunPitchScheduleDialog from "./RunPitchScheduleDialog";
import styles from "./styles";

interface Props {
  open: boolean;
  handleClose: () => void;
  settings: OrganizationBulkSendSettings;
  setSettings: Dispatch<SetStateAction<OrganizationBulkSendSettings | null>>;
  selectedTemplates: Template[];
  setSelectedTemplates: Dispatch<SetStateAction<Template[]>>;
}

export default function UpcomingPitchScheduleDialog({
  open,
  handleClose,
  settings,
  setSettings,
  selectedTemplates,
  setSelectedTemplates,
}: Props) {
  const [loading, setLoading] = useState(false);
  const { getToken } = useAuth();
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg, profile } = useContext(OrganizationUserContext);
  const { outreachTemplates } = useContext(OutreachTemplatesContext);
  const [tempSettings, setTempSettings] =
    useState<OrganizationBulkSendSettings>(settings);

  const [showRunDialog, setShowRunDialog] = useState<boolean>(false);
  const location = getLocation(profile);

  const updateBulkSendSetting = async () => {
    if (!tempSettings?.id) return;

    setLoading(true);

    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bulk-send-settings/${tempSettings?.id}`,
        "PATCH",
        {},
        {
          ...tempSettings,
          timezone: tempSettings?.timezone || moment.tz.guess(),
          templateIds: selectedTemplates?.map((t) => t.id),
        },
      );
      setTempSettings(res.bulkSendSetting);
      setSettings(res.bulkSendSetting);
      setAlert("Successfully updated settings", "success");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimezoneChange = (
    event: CustomEvent,
    newValue: string | null,
  ) => {
    if (newValue) {
      trackEvent("Timezone Changed", { timezone: newValue });
      setTempSettings((prev) => ({
        ...prev,
        timezone: newValue,
      }));
    }
  };

  useEffect(() => {
    if (selectedTemplates?.length === 0 && outreachTemplates?.length > 0) {
      const sortedByLastUsed = outreachTemplates?.sort((a, b) => {
        return (b.lastUsedAt || 0) - (a.lastUsedAt || 0);
      });
      if (sortedByLastUsed?.length > 0) {
        setSelectedTemplates([sortedByLastUsed[0]]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplates?.length, outreachTemplates?.length]);

  useEffect(() => {
    setTempSettings(settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>Set Up Automated Scheduling</DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          All settings related to number of brands and email cycle on a BulkSend
          should be set in the VA Management page
        </Alert>

        <Box sx={{ mb: 3 }}>
          <strong>Select timezone</strong>
          <FormHelperText>
            Emails will be sent between 10:00 AM and 5:00 PM (using local
            timezone).{" "}
            {location && (
              <>
                <br />
                Your location is <strong>{getLocation(profile)}</strong>.
              </>
            )}
          </FormHelperText>

          <TimezoneAutocomplete
            value={tempSettings?.timezone || moment.tz.guess()}
            onChange={handleTimezoneChange}
            renderInput={(params) => (
              <TextField {...params} sx={styles.timezonePicker} />
            )}
            disableClearable
            size="small"
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <strong>Personalize Outreach</strong>
          <Switch
            size="small"
            onChange={(e) => {
              setTempSettings((prev) => ({
                ...prev,
                personalizeOutreach: e.target.checked,
              }));
            }}
            checked={tempSettings?.personalizeOutreach}
          />
          {tempSettings?.personalizeOutreach ? (
            <FormHelperText>
              If you want turn off the personalization feature, toggle the
              switch above.
            </FormHelperText>
          ) : (
            <FormHelperText>
              If you want to turn on the personalization feature, toggle the
              switch above.
            </FormHelperText>
          )}
        </Box>

        <Box>
          <strong>Pause Outreach</strong>
          <Switch
            size="small"
            onChange={(e) => {
              setTempSettings((prev) => ({
                ...prev,
                status: e.target.checked
                  ? BulkSendSettingsStatus.inactive
                  : BulkSendSettingsStatus.active,
              }));
            }}
            checked={tempSettings?.status === BulkSendSettingsStatus.inactive}
          />
          {tempSettings?.status === BulkSendSettingsStatus.inactive ? (
            <FormHelperText>
              If you want to resume your outreach, toggle the switch above.
            </FormHelperText>
          ) : (
            <FormHelperText>
              If you want to temporarily pause your outreach, toggle the switch
              above.
            </FormHelperText>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          color="secondary"
          disableElevation
          disabled={!tempSettings?.templateOption}
          onClick={() => {
            setShowRunDialog(true);
          }}
        >
          Run Now
        </Button>
        <LoadingButton
          variant="contained"
          disableElevation
          disabled={!tempSettings?.templateOption}
          onClick={() => {
            updateBulkSendSetting();
          }}
          loading={loading}
        >
          Confirm
        </LoadingButton>
      </DialogActions>

      {showRunDialog && (
        <RunPitchScheduleDialog
          open={showRunDialog}
          handleClose={() => setShowRunDialog(false)}
        />
      )}
    </Dialog>
  );
}
